import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Fade, Tooltip } from '@mui/material'
import _ from 'lodash'
import { GanttCellRendererProps } from './index'
import GanttDate from './ganttDate'
import { CalendarDateVO } from '../../../../../../../../../domain/value-object/CalendarDateVO'
import objects from '../../../../../../../../../utils/objects'
import { useGanttIndex } from './useGanttIndex'
import { IRowNode } from 'ag-grid-community'
import { GanttDisplayOptions } from '../../../../../../../../../domain/value-object/GanttParameterVO'
import { ProjectPlanCumulation } from '../../../../../../../../../lib/functions/projectPlan'
import {
  AggregateField,
  AggregateTarget,
  WbsItemType,
} from '../../../../../../../../../domain/entity/WbsItemEntity'
import { Progress } from '../../../../../../../../pages/ProjectPlanNew/gridOptions/cumulation'
import {
  SliderWrapper,
  ProgressGauge,
  InsideLabel,
  RightLabel,
  BarHoverArea,
  SummaryProgressRate,
  TypeIcon,
  StatusIcon,
  NameLabel,
  ProgressRateLabel,
  StyledSlider,
  StyledSliderThumb,
  SLIDER_TRACK_CENTER,
} from './ganttSliderStyles'
import { determineLabelMode } from './useLabelMode'
import { useScrollTracking } from './useScrollTracking'
import { useDatePositions } from './GanttBackground'
import { calcTodayXPercent } from './progressLine'
import {
  isGanttSummaryVisualType,
  GanttBarVisualType,
  ShapeStyle,
  getBarColorByType,
  getGanttBarHeight,
  getShapeStyle,
  buildArrowClipPath,
  resolveGanttBaseDate,
} from '../ganttUtil'
import {
  GanttLabelContent,
  hasAnyContent,
  buildHoverText,
} from './GanttLabelContent'
import { WbsItemStatus } from '../../wbsItemStatus'
import { ProgramIcon } from '../../../../../../../../components/icons/ProgramIcon'
import { ProgramItemGroupIcon } from '../../../../../../../../components/icons/ProgramItemGroupIcon'
import { getWbsItemStatusIconColor } from '../../../../../../../../../lib/functions/wbsItem'
import { useDependencyCascadeSlide } from './GanttDependency/useDependencyCascadeSlide'
import { useDependencyCascadeTooltip } from './GanttDependency/useDependencyCascadeTooltip'
import { DependencyCascadeTooltipContent } from './GanttDependency/DependencyCascadeTooltipContent'
import { StyledTooltip } from '../../../../../../../../components/tooltips/Tooltip/MultiLineTooltip'
import {
  ProgressAlertIcon,
  ProgressVarianceDiff,
  formatProgressRate,
} from './progressVariance'
import { GanttRowItem, ganttRowItemService } from '../GanttRowItem'

const MIN_ARROW_PX = 16
const DAY_MS = 86_400_000

const startEditing = (row: IRowNode) => {
  if (!row.id) return
  const ganttCell = document.getElementById(
    `sevend-ag-cell-gantt-chart-${row.id}`
  )
  if (!ganttCell) return
  ganttCell.classList.add('sevend-ag-cell-gantt-chart-editing')
}
const finishEditing = (row: IRowNode) => {
  if (!row.id) return
  const ganttCell = document.getElementById(
    `sevend-ag-cell-gantt-chart-${row.id}`
  )
  if (!ganttCell) return
  ganttCell.classList.remove('sevend-ag-cell-gantt-chart-editing')
}
export interface GanttSliderProps extends GanttCellRendererProps {
  displayOptions?: GanttDisplayOptions
  aggregateField?: AggregateField
  aggregateTarget?: AggregateTarget
  cumulation?: ProjectPlanCumulation
  overrunLeft?: number
  overrunWidth?: number
  hasChildren?: boolean
  visualType: GanttBarVisualType
  rowItem?: GanttRowItem
  /**
   * データ取得経路が GanttRowItem に集約されているか（getGanttRowItem 指定時に true）。
   * true の場合、wbsItem ボディ（`data.body.wbsItem`）前提の直読み・レガシー編集経路・
   * 依存カスケードは使わない。
   */
  rowItemSourced?: boolean
  /**
   * 予定日編集の確定コールバック。指定時は wbsItem 経路への書き込み（ProjectPlanNew 方式）
   * の代わりにこちらを呼ぶ（ProgramManagement のように行直下に予定日を持つデータ用）。
   */
  onScheduledDateEdited?: (
    data: unknown,
    dateTerm: { startDate?: string; endDate?: string }
  ) => void
}

export const calcProgressPositionPercent = (
  scheduledStartDate: Date,
  scheduledEndDate: Date,
  progressRate: number,
  timeScale: CalendarDateVO[]
): number => {
  if (timeScale.length === 0) return 0
  const startMs = scheduledStartDate.getTime()
  const endMs = scheduledEndDate.getTime() + DAY_MS
  if (endMs <= startMs) return 0

  const progressMs = startMs + (endMs - startMs) * progressRate
  const firstMs = timeScale[0].date.getTime()
  const lastMs = timeScale[timeScale.length - 1].date.getTime()
  const unitWidth = 100 / timeScale.length

  if (progressMs < firstMs) {
    const interval =
      timeScale.length > 1 ? timeScale[1].date.getTime() - firstMs : DAY_MS
    return ((progressMs - firstMs) / interval) * unitWidth
  }

  if (progressMs > lastMs) {
    const interval =
      timeScale.length > 1
        ? lastMs - timeScale[timeScale.length - 2].date.getTime()
        : DAY_MS
    return (timeScale.length - 1 + (progressMs - lastMs) / interval) * unitWidth
  }

  for (let i = 0; i < timeScale.length; i++) {
    const dateMs = timeScale[i].date.getTime()
    if (progressMs <= dateMs) {
      if (i === 0) return 0
      const prevMs = timeScale[i - 1].date.getTime()
      const fraction =
        dateMs === prevMs ? 0 : (progressMs - prevMs) / (dateMs - prevMs)
      return (i - 1 + fraction) * unitWidth
    }
  }
  return (timeScale.length - 1) * unitWidth
}

export const GanttSlider = (props: GanttSliderProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const innerLabelRef = useRef<HTMLDivElement>(null)
  const ganttBoundaryRef = useRef<HTMLElement | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [dragType, setDragType] = useState<
    'track' | 'start-thumb' | 'end-thumb' | null
  >(null)
  const [cellWidth, setCellWidth] = useState(0)
  useEffect(() => {
    const el = wrapperRef.current
    if (!el) return
    setCellWidth(el.offsetWidth)
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setCellWidth(entry.contentRect.width)
      }
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  useEffect(() => {
    if (!wrapperRef.current) return
    ganttBoundaryRef.current = wrapperRef.current.closest<HTMLElement>(
      '.ag-center-cols-viewport'
    )
  }, [])

  const isRowItemSourced = !!props.rowItemSourced
  const rowItem = props.rowItem

  // rowItem ソースの行では dependency cascade は不要だが、hooks は無条件に呼ぶ
  const { cascade } = useDependencyCascadeSlide({
    api: props.api,
    timeScale: props.timeScale,
    dataPath: props.dataPath ?? '',
  })
  const dataPath = props.dataPath ? props.dataPath + '.' : ''
  const getWbsItem = (data?: Record<string, unknown>) => {
    if (isRowItemSourced) return undefined
    return objects.getValue(data ?? props.data, `${dataPath}wbsItem`)
  }
  const wbsItemUuid = isRowItemSourced
    ? undefined
    : (getWbsItem()?.uuid as string | undefined)
  const {
    isCtrlPressed,
    dependentItems,
    hasDependents,
    tooltipAnchorEl,
    initPosition,
  } = useDependencyCascadeTooltip({
    isDragging: isRowItemSourced ? false : isDragging,
    dragType: isRowItemSourced ? null : dragType,
    wbsItemUuid,
    api: props.api,
    dataPath: props.dataPath ?? '',
  })

  const getTargetNodes = useCallback(
    () =>
      props.customEditTargetNodeSelection?.getTargetNodes(props) ||
      props.api?.getSelectedNodes(),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- propsオブジェクト全体を指定しつつ個別プロパティも明示。AG Grid ICellRendererParamsの参照は安定
    [props, props.api, props.customEditTargetNodeSelection?.getTargetNodes]
  )

  const startEditingTargetRows = useCallback(() => {
    // 編集不可の行では何もしない（編集可否は GanttRowItem.editable 由来の行単位判定）
    if (!props.editable) return []
    const selectedRows = getTargetNodes()
    if (selectedRows && selectedRows.length > 0) {
      selectedRows.forEach(row => startEditing(row))
    } else {
      startEditing(props.node)
    }
    return selectedRows ?? []
  }, [getTargetNodes, props.node, props.editable])

  // scheduledDate の取得: rowItem ソースでは rowItem から、そうでなければ wbsItem から
  const scheduledDateForGantt = isRowItemSourced
    ? rowItem?.scheduledDate
    : getWbsItem()?.scheduledDate

  const ganttDate = useMemo(
    () =>
      new GanttDate({
        scale: props.timeScale,
        dateTerm: scheduledDateForGantt,
      }),
    [props.value, props.parameter]
  )

  const aggregateField =
    props.aggregateField ?? AggregateField.WBS_ITEM_WORKLOAD
  const aggregateTarget = props.aggregateTarget ?? WbsItemType.TASK
  const { progressRate, scheduledProgressRate, hasChildrenToAggregate } =
    useMemo(() => {
      // rowItem ソースでは rowItem.progressRate を使う
      if (isRowItemSourced && rowItem?.progressRate !== undefined) {
        return {
          progressRate: rowItem.progressRate,
          scheduledProgressRate: rowItem.scheduledProgressRate,
          hasChildrenToAggregate: rowItem.progressRate > 0,
        }
      }
      if (!props.isV2 || !props.cumulation) {
        return {
          progressRate: 0,
          scheduledProgressRate: undefined,
          hasChildrenToAggregate: false,
        }
      }
      const progress = new Progress(
        props.cumulation,
        aggregateTarget,
        aggregateField
      )
      return {
        progressRate: progress.progressRate ?? 0,
        scheduledProgressRate: progress.scheduledProgressRate,
        hasChildrenToAggregate: progress.total > 0,
      }
    }, [
      props.isV2,
      props.cumulation,
      aggregateField,
      aggregateTarget,
      isRowItemSourced,
      rowItem?.progressRate,
      rowItem?.scheduledProgressRate,
    ])

  const progressEndPercent = useMemo(() => {
    if (progressRate <= 0) return 0
    return calcProgressPositionPercent(
      ganttDate.startDate.date,
      ganttDate.endDate.date,
      progressRate,
      props.timeScale
    )
  }, [ganttDate.startDate, ganttDate.endDate, progressRate, props.timeScale])

  const ganttIndex = useGanttIndex(ganttDate)
  const sliderValue = useRef<number[]>([])
  sliderValue.current = ganttIndex.value

  useEffect(() => {
    if (scheduledDateForGantt) {
      ganttIndex.initialize(ganttDate.startIndex, ganttDate.endIndex)
    }
  }, [props.value, props.parameter])

  const onEdit = useCallback(
    (enableCascade: boolean) => {
      if (!props.editable) return
      // 行直下に予定日を持つデータ（ProgramManagement）: 確定値をコールバックで通知する
      if (props.onScheduledDateEdited) {
        finishEditing(props.node)
        // ganttIndex.edit はクローンの wbsItem.scheduledDate に確定値を書き込む
        // （中間オブジェクトは自動生成される）ため、そこから日付だけ取り出す
        const updated = ganttIndex.edit(_.cloneDeep(props.data), dataPath)
        if (updated) {
          const newDate = objects.getValue(
            updated[0],
            `${dataPath}wbsItem.scheduledDate`
          )
          props.onScheduledDateEdited(props.data, {
            startDate: newDate?.startDate,
            endDate: newDate?.endDate,
          })
        }
        return
      }
      // 以降は wbsItem ボディへの直書き編集（ProjectPlanNew 方式）。rowItem ソースでは使えない
      if (isRowItemSourced) return
      const movedRows = ganttIndex.getMovingRows()
      try {
        if (movedRows.length > 0) {
          movedRows.forEach(row => finishEditing(row))
        } else {
          finishEditing(props.node)
        }

        const wbsItemBefore = getWbsItem()
        const oldDate = {
          startDate: wbsItemBefore?.scheduledDate?.startDate
            ? new Date(wbsItemBefore.scheduledDate.startDate)
            : null,
          endDate: wbsItemBefore?.scheduledDate?.endDate
            ? new Date(wbsItemBefore.scheduledDate.endDate)
            : null,
        }

        const updated = ganttIndex.edit(_.cloneDeep(props.data), dataPath)
        if (updated) {
          const newWbsItem = objects.getValue(updated[0], `${dataPath}wbsItem`)
          const newDate = {
            startDate: newWbsItem?.scheduledDate?.startDate
              ? new Date(newWbsItem.scheduledDate.startDate)
              : null,
            endDate: newWbsItem?.scheduledDate?.endDate
              ? new Date(newWbsItem.scheduledDate.endDate)
              : null,
          }

          const cascaded = enableCascade
            ? cascade(updated, oldDate, newDate)
            : []
          props.updateRows([...updated, ...cascaded])
        }
      } finally {
        props.customEditTargetNodeSelection?.rememberTargetNodes?.(movedRows)
      }
    },
    [
      ganttIndex.value,
      props.customEditTargetNodeSelection,
      cascade,
      isRowItemSourced,
    ]
  )

  const onBlurSlider = useCallback(
    () => props.customEditTargetNodeSelection?.clearTargetNodes?.(),
    [props.customEditTargetNodeSelection]
  )

  const trackLeftPercent = (ganttIndex.value[0] / props.timeScale.length) * 100
  const trackRightPercent = (ganttIndex.value[1] / props.timeScale.length) * 100
  const barWidthPercent =
    Math.min(100, trackRightPercent) - Math.max(0, trackLeftPercent)
  const barCellCount = ganttIndex.value[1] - ganttIndex.value[0]
  const barPixelWidth = (cellWidth * barWidthPercent) / 100
  const minArrowPercent =
    barPixelWidth > 0 ? (MIN_ARROW_PX / barPixelWidth) * 100 : 0
  const isLeftEndVisible = ganttIndex.value[0] >= 0
  const isRightEndVisible = ganttIndex.value[1] <= props.timeScale.length
  const arrowWidthPercent =
    barCellCount > 0 && isRightEndVisible
      ? Math.max(100 / barCellCount, minArrowPercent)
      : 0
  // オーバーラン（遅延線）がある場合、right ラベルはオーバーランの右側に配置
  const barEndPercent =
    props.overrunLeft != null && props.overrunWidth != null
      ? props.overrunLeft + props.overrunWidth
      : trackRightPercent
  const isBarVisible =
    ganttIndex.value[0] < props.timeScale.length && ganttIndex.value[1] > 0

  const rightSpacePercent = 100 - barEndPercent

  // rowItem ソースでは wbsItem を使わず rowItem から値を取得
  const wbsItem = isRowItemSourced ? undefined : getWbsItem()
  const statusValue = isRowItemSourced ? rowItem?.status : wbsItem?.status
  const visualType = props.visualType
  const displayOptions = props.displayOptions
  const isSummary = isGanttSummaryVisualType(visualType)

  const isTaskType = visualType === 'task' || visualType === 'parentTask'
  const hasCumulation = hasChildrenToAggregate && !isTaskType
  const isFullyComplete =
    progressRate >= 1 ||
    statusValue === WbsItemStatus.DONE ||
    statusValue === WbsItemStatus.DISCARD
  const showProgressLineLabel =
    !!displayOptions?.showProgressLine &&
    !!displayOptions?.showProgressRate &&
    hasCumulation &&
    !isFullyComplete &&
    isBarVisible &&
    isSummary

  const datePositions = useDatePositions({
    timeScale: props.timeScale,
    unit: props.parameter?.unit,
  })
  const todayEndXPercent = useMemo(() => {
    const today = new Date(resolveGanttBaseDate(props.context?.ganttBaseDate))
    today.setHours(0, 0, 0, 0)
    const endOfToday = CalendarDateVO.of(new Date(today.getTime() + DAY_MS))
    const x = calcTodayXPercent(datePositions, endOfToday)
    return x ?? 0
  }, [datePositions, props.context?.ganttBaseDate])

  const moveLabelToToday =
    !!displayOptions?.showProgressLine &&
    !isSummary &&
    !isFullyComplete &&
    hasCumulation

  const labelMode = determineLabelMode({
    isV2: !!props.isV2,
    isBarVisible,
    isSummary,
    isDragging,
    cellWidth,
    rightSpacePercent,
  })

  const shapeStyle: ShapeStyle = getShapeStyle(visualType)
  const useSvgShape =
    shapeStyle === 'line-triangle' || shapeStyle === 'bar-triangle'
  useScrollTracking(wrapperRef, innerLabelRef, {
    enabled: labelMode === 'inside' && !!props.isV2,
    trackLeftPercent,
    trackRightPercent,
    displayOptions,
    expandToCell: useSvgShape,
  })

  if (
    (props.timeScale.length < ganttDate.startIndex &&
      props.timeScale.length < ganttDate.endIndex) ||
    (ganttDate.startIndex < 0 && ganttDate.endIndex < 0)
  ) {
    return <></>
  }

  const isArrowShape = getShapeStyle(visualType) === 'arrow'
  const scheduledDate = isRowItemSourced
    ? rowItem?.scheduledDate
    : wbsItem?.scheduledDate
  const isScheduled = !!scheduledDate?.startDate && !!scheduledDate?.endDate
  const arrowOverlapSliderPct =
    isArrowShape && arrowWidthPercent > 0 && isScheduled
      ? (arrowWidthPercent / 100) * barWidthPercent
      : 0
  // 行単位の編集可否（GanttRowItem.editable 由来）。readOnly 画面でも編集可の行はある
  const isEditable = !!props.editable
  const slider = (
    <StyledSlider
      key={`gantt-slider-${props.node?.rowIndex}`}
      value={ganttIndex.value}
      size="small"
      components={{ Thumb: StyledSliderThumb }}
      valueLabelDisplay="auto"
      valueLabelFormat={(v, index) => {
        return index === 0
          ? ganttDate.getStartDateByIndex(v).format('M/D (ddd)')
          : ganttDate.getEndDateByIndex(v).format('M/D (ddd)')
      }}
      onBlur={onBlurSlider}
      componentsProps={
        isEditable
          ? {
              track: {
                onMouseDown: (e: React.MouseEvent) => {
                  initPosition(e.clientX, e.clientY)
                  setIsDragging(true)
                  setDragType('track')
                  const selectedRows = startEditingTargetRows()
                  ganttIndex.moveWithTrack(selectedRows)
                },
              },
              thumb: {
                onMouseDown: (e: React.MouseEvent<HTMLSpanElement>) => {
                  initPosition(e.clientX, e.clientY)
                  setIsDragging(true)
                  const index = Number(
                    e.currentTarget.getAttribute('data-index')
                  )
                  setDragType(index === 0 ? 'start-thumb' : 'end-thumb')
                  const selectedRows = startEditingTargetRows()
                  ganttIndex.moveWithThumb(selectedRows)
                },
              },
            }
          : undefined
      }
      onChange={(event: Event, val: number | number[], activeThumb: number) => {
        if (!isEditable) return
        const indexes = typeof val === 'number' ? [val, val] : val
        if (event.type === 'mousedown') {
          ganttIndex.setMoveWithTrackOrigin(indexes, activeThumb)
        }
        if (event.type !== 'mousemove') return
        ganttIndex.move(indexes, activeThumb)
      }}
      onChangeCommitted={(event: React.SyntheticEvent | Event) => {
        setIsDragging(false)
        setDragType(null)
        if (!isEditable) return
        const nativeEvent = 'nativeEvent' in event ? event.nativeEvent : event
        const ctrlKey =
          nativeEvent instanceof MouseEvent &&
          (nativeEvent.ctrlKey || nativeEvent.metaKey)
        onEdit(ctrlKey)
      }}
      max={props.timeScale.length}
      visualtype={visualType}
      scheduled={+isScheduled}
      disablethumbs={[!!ganttIndex.startDisabled, !!ganttIndex.endDisabled]}
      index={props.node?.rowIndex ?? 0}
      isv2={props.isV2 ? 1 : 0}
      overrunleft={
        props.overrunLeft != null
          ? props.overrunLeft - arrowOverlapSliderPct
          : undefined
      }
      overrunwidth={
        props.overrunWidth != null
          ? props.overrunWidth + arrowOverlapSliderPct
          : undefined
      }
      arrowoverlap={arrowOverlapSliderPct || undefined}
      wbsitemdone={statusValue === WbsItemStatus.DONE ? 1 : 0}
      arrowwidth={arrowWidthPercent}
      // 予定線が細い行（プログラム管理）でも超過ハッチが潰れないよう最低太さを指定する
      hatchminheight={isRowItemSourced ? 12 : 0}
      bareditable={isEditable ? 1 : 0}
    />
  )

  if (!props.isV2) {
    return (
      <Fade in={true} timeout={300}>
        {slider}
      </Fade>
    )
  }

  const visibleLeft = Math.max(0, trackLeftPercent)
  const useArrowShape = shapeStyle === 'arrow'
  const usePillShape = shapeStyle === 'pill'
  const barH = getGanttBarHeight(visualType)

  const labelContentProps = {
    isSummary,
    displayOptions,
    rowItem,
    progressRate,
    scheduledProgressRate,
    labelMode,
    hasCumulation,
    omitProgressRate: showProgressLineLabel,
  }
  const hasInfoContent = hasAnyContent(labelContentProps)
  const rightSpacePx = (rightSpacePercent * cellWidth) / 100
  const iconUrl = ganttRowItemService.getIconUrl(rowItem)
  // PROGRAM/GROUP 行（プログラム管理画面）は iconUrl を持たないため専用アイコンで代替する
  const fallbackTypeIcon = ganttRowItemService.isProgram(rowItem) ? (
    <ProgramIcon size={16} />
  ) : ganttRowItemService.isGroup(rowItem) ? (
    <ProgramItemGroupIcon size={16} />
  ) : null
  // フォールバックアイコンはバー左端のラベル内に出るため右余白（rightSpacePx）に依存させない
  // （バーが表示範囲右端まで伸びる GROUP/PROGRAM 行でも表示されるようにする）
  const showTypeIcon =
    displayOptions?.showTypeIcon &&
    ((!!iconUrl && rightSpacePx >= 28) || !!fallbackTypeIcon)
  const showProgress =
    displayOptions?.showProgressRate &&
    hasCumulation &&
    rightSpacePx >= 60 &&
    !showProgressLineLabel
  // ステータス丸は WbsItem のステータス体系（色マッピング）前提のため、
  // プログラム管理画面の PROGRAM/GROUP/PROJECT 行では表示しない
  const showStatusIcon =
    !!displayOptions?.showStatus &&
    !!rowItem?.status &&
    !ganttRowItemService.isProgram(rowItem) &&
    !ganttRowItemService.isGroup(rowItem) &&
    !ganttRowItemService.isProject(rowItem)
  const hasSummaryInsideContent =
    (displayOptions?.showName && !!rowItem?.displayName) ||
    showTypeIcon ||
    showStatusIcon

  const renderProgressWithVariance = (isSummary: boolean) => (
    <>
      <ProgressAlertIcon
        progressRate={progressRate}
        scheduledProgressRate={scheduledProgressRate}
        isSummary={isSummary}
      />
      <ProgressRateLabel
        inside={false}
        isSummary={isSummary}
        showSeparator={false}
      >
        {formatProgressRate(progressRate)}
      </ProgressRateLabel>
      <ProgressVarianceDiff
        progressRate={progressRate}
        scheduledProgressRate={scheduledProgressRate}
        isSummary={isSummary}
      />
    </>
  )

  const hoverText = buildHoverText(labelContentProps)
  const tooltipProps = {
    title: hoverText,
    placement: 'top' as const,
    arrow: true,
    enterDelay: 500,
    enterNextDelay: 500,
    componentsProps: { tooltip: { sx: { maxWidth: 450 } } },
    PopperProps: {
      modifiers: [
        {
          name: 'preventOverflow' as const,
          options: { boundary: ganttBoundaryRef.current ?? undefined },
        },
      ],
    },
  }

  // バー本体（BarHoverArea/SVGオーバーレイ）への mousedown を track に転送してスライドを開始する。
  // 編集可否は行単位（GanttRowItem.editable 由来の isEditable）で判定する
  const forwardMouseDownToTrack = (e: React.MouseEvent) => {
    if (!isEditable) return
    const track = wrapperRef.current?.querySelector(
      '.MuiSlider-track'
    ) as HTMLElement
    if (track) {
      track.dispatchEvent(
        new MouseEvent('mousedown', {
          bubbles: true,
          clientX: e.clientX,
          clientY: e.clientY,
          button: e.button,
        })
      )
    }
  }

  const buildProgressGaugeStyle = (): React.CSSProperties => {
    const gaugeLeft = Math.max(0, trackLeftPercent)
    const progressW = Math.max(0, Math.min(100, progressEndPercent) - gaugeLeft)
    const doneColor = getBarColorByType(visualType, true)
    if (useArrowShape) {
      const progressPct =
        barWidthPercent > 0 ? (progressW / barWidthPercent) * 100 : 0
      const arrowClipPath =
        isRightEndVisible && arrowWidthPercent > 0
          ? buildArrowClipPath(arrowWidthPercent)
          : undefined
      return {
        left: `${gaugeLeft}%`,
        width: `${barWidthPercent}%`,
        background: `linear-gradient(to right, ${doneColor} ${progressPct}%, transparent ${progressPct}%)`,
        clipPath: arrowClipPath,
        borderRadius: '2px',
      }
    }
    const reachesRightEnd =
      progressEndPercent >= trackRightPercent ||
      statusValue === WbsItemStatus.DONE
    const rightR = reachesRightEnd ? barH / 2 : 2
    return {
      left: `${gaugeLeft}%`,
      width: `${progressW}%`,
      backgroundColor: doneColor,
      borderRadius: useSvgShape
        ? `${barH / 2}px ${rightR}px 0 0`
        : usePillShape
          ? `${barH / 2}px`
          : '2px',
    }
  }

  const renderSvgOverlay = () => {
    if (!useSvgShape || !isBarVisible || cellWidth <= 0) return null
    if (!isScheduled) return null
    const leftPx = (Math.max(0, trackLeftPercent) / 100) * cellWidth
    const rightPx = (Math.min(100, trackRightPercent) / 100) * cellWidth
    const barWidth = Math.max(0, rightPx - leftPx)
    const isDone = statusValue === WbsItemStatus.DONE
    const barColor = getBarColorByType(visualType, isDone)
    const gaugeColor = getBarColorByType(visualType, true)
    const baseTriangleColor = getBarColorByType(visualType, false)
    const barOpacity = isDone ? 1.0 : 0.85
    const leftUsesGauge = progressRate > 0 || isDone
    const leftTriangleColor = leftUsesGauge ? gaugeColor : baseTriangleColor
    const progressReachesEnd =
      progressRate >= 1 || progressEndPercent >= trackRightPercent
    const rightUsesGauge = (progressRate > 0 && progressReachesEnd) || isDone
    const rightTriangleColor = rightUsesGauge ? gaugeColor : baseTriangleColor

    const hasTriangle =
      shapeStyle === 'line-triangle' || shapeStyle === 'bar-triangle'
    const tabS = Math.min(barH, 8, barWidth / 2)
    const showTriangles = hasTriangle && tabS >= 2
    const rMax = Math.min(barH / 2, barWidth / 2)
    const rLeft = isLeftEndVisible ? rMax : 0
    const rRight = isRightEndVisible ? rMax : 0
    const topRoundedPath = `M ${leftPx + rLeft} 0 H ${leftPx + barWidth - rRight} A ${rRight} ${rRight} 0 0 1 ${leftPx + barWidth} ${rRight} V ${barH} H ${leftPx} V ${rLeft} A ${rLeft} ${rLeft} 0 0 1 ${leftPx + rLeft} 0 Z`

    return (
      <svg
        style={{
          position: 'absolute',
          top: `${SLIDER_TRACK_CENTER}px`,
          transform: `translateY(-${barH / 2}px)`,
          left: 0,
          width: '100%',
          height: `${barH + (showTriangles ? tabS : 0)}px`,
          pointerEvents: 'none',
          zIndex: 0,
          overflow: 'visible',
        }}
      >
        <g opacity={isDone ? 1.0 : 0.85}>
          <path d={topRoundedPath} fill={barColor} />
        </g>
        {showTriangles && isLeftEndVisible && (
          <polygon
            points={`${leftPx},${barH - 0.5} ${leftPx + tabS},${barH - 0.5} ${leftPx},${barH - 0.5 + tabS}`}
            fill={leftTriangleColor}
            opacity={leftUsesGauge ? 1 : barOpacity}
            shapeRendering="crispEdges"
          />
        )}
        {showTriangles && isRightEndVisible && (
          <polygon
            points={`${rightPx - tabS},${barH - 0.5} ${rightPx},${barH - 0.5} ${rightPx},${barH - 0.5 + tabS}`}
            fill={rightTriangleColor}
            opacity={rightUsesGauge ? 1 : barOpacity}
            shapeRendering="crispEdges"
          />
        )}
      </svg>
    )
  }

  const showCascadeTooltip = !isRowItemSourced && isDragging && hasDependents

  return (
    <StyledTooltip
      open={showCascadeTooltip}
      title={
        showCascadeTooltip ? (
          <DependencyCascadeTooltipContent
            isCtrlPressed={isCtrlPressed}
            dependentItems={dependentItems}
          />
        ) : (
          ''
        )
      }
      placement="top"
      arrow={true}
      disableHoverListener={true}
      disableFocusListener={true}
      PopperProps={{
        anchorEl: tooltipAnchorEl,
      }}
      componentsProps={{
        tooltip: {
          sx: {
            '&.MuiTooltip-tooltip': {
              maxHeight: 'none',
              overflowY: 'visible',
            },
          },
        },
      }}
    >
      <Fade in={true} timeout={300}>
        <SliderWrapper ref={wrapperRef}>
          {slider}
          {renderSvgOverlay()}
          {progressRate > 0 &&
            isBarVisible &&
            props.isV2 &&
            scheduledDate?.startDate &&
            scheduledDate?.endDate && (
              <ProgressGauge
                style={buildProgressGaugeStyle()}
                barHeight={barH}
              />
            )}
          {isBarVisible && isSummary && (
            <>
              <Tooltip {...tooltipProps}>
                <BarHoverArea
                  left={visibleLeft}
                  width={barWidthPercent}
                  isSummary={true}
                  onMouseDown={forwardMouseDownToTrack}
                />
              </Tooltip>
              {hasSummaryInsideContent && (
                <Tooltip {...tooltipProps}>
                  <InsideLabel
                    ref={innerLabelRef}
                    left={visibleLeft}
                    width={useSvgShape ? 100 - visibleLeft : barWidthPercent}
                    isSummary={true}
                    style={
                      useSvgShape
                        ? { transform: 'translateY(-90%)' }
                        : undefined
                    }
                  >
                    {showTypeIcon &&
                      (iconUrl ? (
                        <TypeIcon
                          src={iconUrl}
                          rounded={ganttRowItemService.isProject(rowItem)}
                        />
                      ) : (
                        fallbackTypeIcon
                      ))}
                    {showStatusIcon && rowItem?.status && (
                      <StatusIcon
                        color={getWbsItemStatusIconColor(
                          rowItem.status as WbsItemStatus
                        )}
                      />
                    )}
                    {displayOptions?.showName && rowItem?.displayName && (
                      <NameLabel inside={!useSvgShape} isSummary={true}>
                        {rowItem.displayName}
                      </NameLabel>
                    )}
                  </InsideLabel>
                </Tooltip>
              )}
              {showProgress && (
                <SummaryProgressRate
                  left={barEndPercent}
                  style={
                    useSvgShape
                      ? { transform: 'translateY(calc(-50% + 8px))' }
                      : undefined
                  }
                >
                  {renderProgressWithVariance(true)}
                </SummaryProgressRate>
              )}
            </>
          )}
          {isBarVisible && !isSummary && hasInfoContent && (
            <Tooltip {...tooltipProps}>
              {labelMode === 'right' || moveLabelToToday ? (
                <RightLabel
                  left={moveLabelToToday ? todayEndXPercent : barEndPercent}
                  isSummary={false}
                  maxWidth={`${rightSpacePercent}%`}
                  centerOnLeft={moveLabelToToday}
                >
                  <GanttLabelContent {...labelContentProps} />
                </RightLabel>
              ) : (
                <BarHoverArea
                  left={visibleLeft}
                  width={barWidthPercent}
                  onMouseDown={forwardMouseDownToTrack}
                />
              )}
            </Tooltip>
          )}
        </SliderWrapper>
      </Fade>
    </StyledTooltip>
  )
}
